import React, { useEffect, useState } from "react";
import styles from "./PresentationLayout.module.css";
import { useSelector } from "react-redux";
import { isMobile } from "react-device-detect";
import {
  Room,
  RemoteParticipant as TwilioRemoteParticipant,
} from "twilio-video";
import { RootState } from "store/reducers";
import { RemoteParticipant } from "../remote-participant/RemoteParticipant";
import { FocusedParticipant } from "../focused-participant/FocusedParticipant";
import { LocalParticipant } from "../local-participant/LocalParticipant";
import { RemoteParticipantData } from "store/remote-participants/types";
import { useParticipantsInAreaFilter } from "hooks/useParticipantsInAreaFilter";
import { useInteractivityListeners } from "hooks/useInteractivityListeners";
import { ChatBox } from "../chat/ChatBox";
import {ShareScreenBtnGroup} from "../../shared/btn-group/ShareScreenBtnGroup";

interface PresentationLayoutProps {
  room: Room;
  participantsInArea: string[];
  findPredicate: (data: RemoteParticipantData) => boolean;
  filterCallback?: (
    value: RemoteParticipantData,
    index: number,
    array: RemoteParticipantData[]
  ) => boolean;
}
export const PresentationLayout: React.FC<PresentationLayoutProps> = ({
  room,
  participantsInArea,
  findPredicate,
  filterCallback,
}) => {
  const { participants } = useSelector((s: RootState) => s.remoteParticipants);
  const { privateChatEnabled } = useSelector((s: RootState) => s.user);
  const { showChat } = useSelector((s: RootState) => s.chatMessages);
  const [focusedParticipant, setFocusedParticipant] = useState<{
    participant?: TwilioRemoteParticipant;
    participantState?: RemoteParticipantData;
  }>();
  const { participantsInAreaFilter } = useParticipantsInAreaFilter(
    participantsInArea
  );
  useInteractivityListeners(styles["program-feed-layout"]);

  useEffect(() => {
    const participantState = participants.find(findPredicate);
    if (participantState?.identity) {
      const participant = room.participants.get(participantState.sid);
      setFocusedParticipant({ participant, participantState });
    }
  }, [participants, findPredicate, room]);

  return (
    <div className={styles["program-feed-layout"]}>
      {focusedParticipant?.participant &&
        focusedParticipant.participantState && (
          <FocusedParticipant
            key={focusedParticipant?.participant?.sid + "-focus-tracks"}
            participant={focusedParticipant.participant}
            participantState={focusedParticipant.participantState}
          />
        )}

      {isMobile && (
        <LocalParticipant
          key={room.localParticipant.sid + "-local-participant"}
          participant={room.localParticipant}
          inSidebar={false}
        />
      )}
      <div
        className={`${styles["remote-participants-sidebar"]} ${
          isMobile ? styles["hide"] : ""
        }`}
      >
        {!isMobile && (
          <LocalParticipant
            key={room.localParticipant.sid + "-local-participant"}
            participant={room.localParticipant}
            inSidebar={true}
          />
        )}
        {privateChatEnabled && <ShareScreenBtnGroup />}
        <span className={`${styles["participant-wrapper"]} ${showChat ? "" : styles["show"]}`}>
          {filterCallback
            ? participants
              .filter(participantsInAreaFilter)
              .filter(filterCallback)
              .sort((a) => {
                return a.videoIsEnabled && !a.isSwitchedOff ? -1 : 1;
              })
              .map((p) => {
                const participant = room.participants.get(p.sid);
                if (participant) {
                  return (
                    <RemoteParticipant
                      key={participant.sid + "-remote-tracks"}
                      participant={participant}
                      participantState={p}
                      inSidebar={true}
                      isDominantSpeaker={
                        room?.dominantSpeaker?.sid === participant.sid
                      }
                    />
                  );
                }
                return null;
              })
            : participants
              .filter(participantsInAreaFilter)
              .sort((a) => {
                return a.videoIsEnabled && !a.isSwitchedOff ? -1 : 1;
              })
              .map((p) => {
                const participant = room.participants.get(p.sid);
                if (participant) {
                  return (
                    <RemoteParticipant
                      key={participant.sid}
                      participant={participant}
                      participantState={p}
                      inSidebar={true}
                    />
                  );
                }
                return null;
              })}
        </span>
        {privateChatEnabled && 
          <span className={`${styles["chat-wrapper"]} ${showChat ? styles["show"] : ""}`}>
            <ChatBox
              isShareLayout={true}
            />
          </span>
        }
      </div>
    </div>
  );
};
